import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Flame, LogOut, Settings } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useDashboard } from '@/api/use-dashboard';
import { useAuth } from '@/contexts/AuthContext';

const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/items': 'Items',
  '/scenes': 'Scene Library',
  '/logs': 'Training Logs',
  '/stats': 'Progress Statistics',
  '/books': 'Book Management',
  '/settings': 'Settings',
};

export function Header() {
  const location = useLocation();
  const { data: dashboard } = useDashboard();
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const title = PAGE_TITLES[location.pathname] ?? 'Cadence';
  const streak = dashboard?.overview?.streak ?? 0;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const initials = user?.display_name
    ? user.display_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.github_login?.[0]?.toUpperCase() ?? 'U';

  const handleLogout = async () => {
    setShowMenu(false);
    await logout();
    window.location.href = '/login';
  };

  return (
    <header className="h-16 border-b border-slate-200/50 bg-white/80 backdrop-blur-sm flex items-center justify-between px-8 sticky top-0 z-10">
      <h1 className="text-base font-bold text-slate-900 tracking-tight">{title}</h1>
      <div className="flex items-center gap-4">
        {streak > 0 && (
          <div className="flex items-center gap-1.5 bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full text-xs font-bold">
            <Flame className="w-3 h-3" />
            {streak} Day Streak
          </div>
        )}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <span className="text-sm text-slate-600 hidden sm:block">
              {user?.display_name ?? user?.github_login ?? 'User'}
            </span>
            <Avatar className="w-8 h-8 border border-slate-200">
              {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user.display_name ?? ''} />}
              <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
          </button>

          {showMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border py-1 z-50">
              <a
                href="/settings"
                className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => setShowMenu(false)}
              >
                <Settings className="w-4 h-4" /> Settings
              </a>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" /> Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
